// ⚠️ ARCHIVO GENERADO — NO LO EDITES AQUÍ.
// Fuente: /root/sites/_shared/lealify-campaigns/src/campaignVocab.ts
// Para cambiarlo: edita la fuente y corre /root/sites/_shared/lealify-campaigns/sync.sh
// El deploy verifica esta copia con `sync.sh --check` y falla si difiere.
// lealify-campaigns/src/campaignVocab.ts — FUENTE DE VERDAD
// ============================================================================
// VOCABULARIO DE CAMPAÑAS — estados, etiquetas y permisos.
//
// Este archivo se COPIA a los dos fronts con ./sync.sh. No edites las copias:
// llevan una cabecera que lo advierte y `./sync.sh --check` (que corre en cada
// deploy) falla si alguien las tocó. Hasta el 24-jul-2026 la sincronización era
// a mano y funcionaba de milagro: cada front tenía su propia versión con una
// cabecera apuntando a la otra.
//
// A propósito NO trae clases de CSS: la agenda usa tokens (`text-warning`) y el
// manager usa Tailwind crudo (`text-yellow-700`). Aquí vive el SIGNIFICADO
// (`tono: 'warn'`); cada front lo pinta con su paleta.
//
// DOS EJES (F2, 23-jul-2026):
//   · FICHA  → `archived_at`: la campaña está en circulación o guardada.
//   · ENVÍO  → `status` del motor: qué está haciendo la cola ahora mismo.
// Son independientes: una campaña archivada conserva enviados y pendientes.
// ============================================================================

export type MotorStatus =
  | 'draft' | 'scheduled' | 'running' | 'paused' | 'paused_error' | 'done' | 'cancelled';

export type Tono = 'ok' | 'info' | 'warn' | 'error' | 'neutro';

export interface EstadoEnvio {
  emoji: string;
  label: string;
  tono: Tono;
  /** Explicación en una frase, para el `title` del chip. */
  ayuda: string;
}

/** Eje 2 — qué está haciendo el envío. Un estado del motor, una etiqueta. */
export const ENVIO: Record<MotorStatus, EstadoEnvio> = {
  draft: {
    emoji: '📝', label: 'Sin programar', tono: 'neutro',
    ayuda: 'Todavía no se ha activado: no hay nadie en la cola.',
  },
  scheduled: {
    emoji: '📅', label: 'Programada', tono: 'info',
    ayuda: 'Activada y en espera: empezará a enviar en su próxima ventana.',
  },
  running: {
    emoji: '📤', label: 'En curso', tono: 'ok',
    ayuda: 'Enviando ahora mismo, al ritmo permitido por los topes anti-bloqueo.',
  },
  paused: {
    emoji: '⏸️', label: 'Pausada', tono: 'warn',
    ayuda: 'Detenida a propósito. No se envía nada; la cola se conserva completa.',
  },
  paused_error: {
    emoji: '⚠️', label: 'Pausada por error', tono: 'error',
    ayuda: 'Se detuvo sola tras varios fallos seguidos (normalmente WhatsApp desconectado).',
  },
  done: {
    emoji: '✅', label: 'Finalizada', tono: 'neutro',
    ayuda: 'Terminó: ya se envió a toda su audiencia.',
  },
  cancelled: {
    emoji: '🚫', label: 'Cancelada', tono: 'neutro',
    ayuda: 'Se canceló y sus mensajes pendientes se descartaron.',
  },
};

const esMotorStatus = (s?: string | null): s is MotorStatus =>
  !!s && Object.prototype.hasOwnProperty.call(ENVIO, s);

/** Chip del envío. `null` si la campaña no viene del motor (sistema clásico). */
export const chipEnvio = (motorStatus?: string | null): EstadoEnvio | null =>
  esMotorStatus(motorStatus) ? ENVIO[motorStatus] : null;

/** Texto listo para pintar: "📤 En curso". */
export const chipEnvioTexto = (motorStatus?: string | null): string => {
  const e = chipEnvio(motorStatus);
  return e ? `${e.emoji} ${e.label}` : '';
};

/** Eje 1 — la ficha. Archivada gana sobre todo: es una decisión del usuario.
 *  Después distingue terminada de cancelada, que tampoco son "archivadas". */
export const etiquetaFicha = (motorStatus?: string | null, archivedAt?: string | null): string => {
  if (archivedAt) return 'Archivada';
  if (motorStatus === 'done') return 'Finalizada';
  if (motorStatus === 'cancelled') return 'Cancelada';
  if (motorStatus === 'draft') return 'Borrador';
  return 'Activa';
};

/** ¿Sigue en circulación? Determina en qué pestaña cae. */
export const enCirculacion = (motorStatus?: string | null, archivedAt?: string | null): boolean =>
  !archivedAt && motorStatus !== 'done' && motorStatus !== 'cancelled';

// ── Permisos. Se deciden por el estado CRUDO del motor, nunca por el de la UI ──
// Sin `motorStatus` (sistema clásico, sin motor) se conserva el criterio histórico
// permisivo: el front clásico gestionaba el estado por su cuenta.

export const puedePausar = (motorStatus?: string | null, archivedAt?: string | null): boolean =>
  !archivedAt && (esMotorStatus(motorStatus)
    ? ['scheduled', 'running'].includes(motorStatus)
    : true);

export const puedeReanudar = (motorStatus?: string | null, archivedAt?: string | null): boolean =>
  !archivedAt && esMotorStatus(motorStatus) && ['paused', 'paused_error'].includes(motorStatus);

export const puedeArchivar = (_motorStatus?: string | null, archivedAt?: string | null): boolean =>
  !archivedAt;

export const puedeRestaurar = (_motorStatus?: string | null, archivedAt?: string | null): boolean =>
  !!archivedAt;

/** Editar el contenido: el motor solo lo permite si no está enviando. */
export const puedeEditar = (motorStatus?: string | null): boolean =>
  esMotorStatus(motorStatus)
    ? ['draft', 'paused', 'paused_error', 'cancelled'].includes(motorStatus)
    : true;

/** Enviar (asistido o automático) exige envío activo Y ficha sin archivar. */
export const puedeEnviar = (motorStatus?: string | null, archivedAt?: string | null): boolean =>
  !archivedAt && esMotorStatus(motorStatus) && ['scheduled', 'running'].includes(motorStatus);

/** Por qué no se puede enviar, dicho para el cliente. `null` si sí se puede. */
export const motivoNoEnviar = (motorStatus?: string | null, archivedAt?: string | null): string | null => {
  if (archivedAt) return 'Esta campaña está archivada. Restáurala para poder enviar.';
  if (!esMotorStatus(motorStatus)) return null;
  if (motorStatus === 'paused') return 'El envío está pausado. Reanúdalo para poder enviar.';
  if (motorStatus === 'paused_error') return 'El envío se detuvo por errores. Revisa WhatsApp y reanúdalo.';
  if (motorStatus === 'done') return 'Esta campaña ya terminó de enviar.';
  if (motorStatus === 'cancelled') return 'Esta campaña está cancelada.';
  if (motorStatus === 'draft') return 'Actívala para empezar a enviar.';
  return null;
};

// ── Textos de las acciones. Mismos verbos en los dos productos ──
export const ACCIONES = {
  activar:   { label: 'Activar',         title: 'Activar y empezar a enviar' },
  ver:       { label: 'Ver audiencia',   title: 'Ver a quién le toca y enviar' },
  editar:    { label: 'Editar',          title: 'Editar la campaña' },
  pausar:    { label: 'Pausar envío',    title: 'Detener el envío. La cola se conserva completa.' },
  reanudar:  { label: 'Reanudar envío',  title: 'Seguir enviando desde donde se quedó' },
  archivar:  { label: 'Archivar',        title: 'Guardarla y sacarla de circulación. No se pierde nada.' },
  restaurar: { label: 'Restaurar',       title: 'Devolverla a circulación tal como estaba' },
  cancelar:  { label: 'Cancelar',        title: 'Descartar los mensajes pendientes' },
} as const;

const plural = (n: number, sing: string, plur: string) => `${n} ${n === 1 ? sing : plur}`;

/** Confirmación de ARCHIVAR — no destructiva, y se dice explícitamente. */
export const confirmArchivar = (nombre: string, pendientes = 0): string =>
  `Archivar "${nombre}".\n\n` +
  `Se guarda y deja de enviar` +
  (pendientes > 0
    ? `; ${plural(pendientes, 'mensaje pendiente se conserva', 'mensajes pendientes se conservan')} tal cual.\n\n`
    : `.\n\n`) +
  `No se borra nada: al restaurarla vuelve exactamente como está ahora.`;

/** Confirmación de CANCELAR — sí es destructiva, y hay que decir el número. */
export const confirmCancelar = (nombre: string, pendientes = 0): string =>
  `Cancelar "${nombre}".\n\n` +
  (pendientes > 0
    ? `⚠️ Se descartarán ${plural(pendientes, 'mensaje pendiente', 'mensajes pendientes')}. No se recuperan.\n\n`
    : `No hay mensajes pendientes que descartar.\n\n`) +
  `Lo ya enviado se conserva en el historial. ¿Continuar?`;

/** Confirmación de RESTAURAR. */
export const confirmRestaurar = (nombre: string): string =>
  `Restaurar "${nombre}".\n\n` +
  `Vuelve a circulación exactamente como estaba (mismos enviados y pendientes), en pausa. ` +
  `Tú decides cuándo reanudar el envío.`;
